import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatMoney } from '@/utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminWebTraderPreview = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await axios.get(`${API}/admin/clients`);
        setClients(response.data);
      } catch (e) {
        toast.error('Failed to load clients');
      }
    })();
  }, []);

  const loadPreview = async (clientId) => {
    setSelectedClient(clientId);
    if (!clientId) { setSnapshot(null); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/webtrader/preview/${clientId}`);
      setSnapshot(response.data);
    } catch (e) {
      toast.error('Failed to load preview');
    } finally { setLoading(false); }
  };

  return (
    <div className="p-8" data-testid="admin-webtrader-preview-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-primary">WebTrader Preview</h1>
        <p className="text-slate-600 mt-1">View the WebTrader environment exactly as the client sees it</p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-slate-200 mb-6">
        <label className="text-sm font-semibold text-slate-700">Select Client</label>
        <Select value={selectedClient} onValueChange={loadPreview}>
          <SelectTrigger className="max-w-md mt-2" data-testid="preview-client-select"><SelectValue placeholder="Choose a client..." /></SelectTrigger>
          <SelectContent>
            {clients.map(c => (
              <SelectItem key={c.id} value={c.id}>{c.full_name} — {c.email}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="loader mx-auto"></div>}

      {snapshot && (
        <div className="space-y-6" data-testid="preview-snapshot">
          {/* Client info */}
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-bold mb-4">Client</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div><div className="text-slate-500">Name</div><div className="font-semibold">{snapshot.client.full_name}</div></div>
              <div><div className="text-slate-500">Email</div><div className="font-semibold">{snapshot.client.email}</div></div>
              <div><div className="text-slate-500">Country</div><div className="font-semibold">{snapshot.client.country || '-'}</div></div>
              <div><div className="text-slate-500">KYC</div><div className="font-semibold">{snapshot.client.kyc_status}</div></div>
            </div>
          </div>

          {/* Accounts */}
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-bold mb-4">Trading Accounts</h2>
            {snapshot.accounts.length === 0 ? <p className="text-slate-500">No accounts</p> : (
              <div className="grid md:grid-cols-2 gap-4">
                {snapshot.accounts.map(a => (
                  <div key={a.id} className="p-4 border border-slate-200 rounded-lg">
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold">{a.account_type}</span>
                      <span className="text-xs">1:{a.leverage}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><div className="text-slate-500 text-xs">Balance</div><div className="font-semibold tabular-nums">${a.balance?.toFixed(2)}</div></div>
                      <div><div className="text-slate-500 text-xs">Equity</div><div className="font-semibold tabular-nums">${a.equity?.toFixed(2)}</div></div>
                      <div><div className="text-slate-500 text-xs">Margin</div><div className="tabular-nums">${a.margin?.toFixed(2)}</div></div>
                      <div><div className="text-slate-500 text-xs">Free</div><div className="tabular-nums">${a.free_margin?.toFixed(2)}</div></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Positions */}
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-bold mb-4">Open Positions ({snapshot.positions.length})</h2>
            {snapshot.positions.length === 0 ? <p className="text-slate-500">No open positions</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                    <th className="p-2">Symbol</th><th className="p-2">Dir</th><th className="p-2">Volume</th>
                    <th className="p-2 text-right">Open</th><th className="p-2 text-right">Current</th><th className="p-2 text-right">P/L</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.positions.map(p => (
                    <tr key={p.id} className="border-t border-slate-100">
                      <td className="p-2 font-semibold">{p.symbol}</td>
                      <td className="p-2"><span className={`px-2 py-1 rounded text-xs ${p.direction === 'BUY' ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'}`}>{p.direction}</span></td>
                      <td className="p-2 tabular-nums">{p.volume}</td>
                      <td className="p-2 text-right tabular-nums">{p.open_price?.toFixed(5)}</td>
                      <td className="p-2 text-right tabular-nums">{p.current_price?.toFixed(5)}</td>
                      <td className={`p-2 text-right font-semibold tabular-nums ${p.profit_loss >= 0 ? 'text-trading-bullish' : 'text-trading-bearish'}`}>{formatMoney(p.profit_loss, p.account_currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Orders */}
          <div className="bg-white p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-bold mb-4">Recent Orders ({snapshot.orders.length})</h2>
            {snapshot.orders.length === 0 ? <p className="text-slate-500">No orders</p> : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs uppercase text-slate-600">
                    <th className="p-2">Symbol</th><th className="p-2">Type</th><th className="p-2">Dir</th><th className="p-2">Volume</th><th className="p-2">Status</th><th className="p-2">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {snapshot.orders.slice(0, 10).map(o => (
                    <tr key={o.id} className="border-t border-slate-100">
                      <td className="p-2 font-semibold">{o.symbol}</td>
                      <td className="p-2">{o.order_type}</td>
                      <td className="p-2">{o.direction}</td>
                      <td className="p-2 tabular-nums">{o.volume}</td>
                      <td className="p-2">{o.status}</td>
                      <td className="p-2 text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Settings summary */}
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
            <h2 className="text-xl font-bold mb-4">Current WebTrader Configuration</h2>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div><div className="text-slate-500 mb-1">Enabled Categories</div><div>{snapshot.settings.enabled_categories?.join(', ')}</div></div>
              <div><div className="text-slate-500 mb-1">Enabled Timeframes</div><div>{snapshot.settings.enabled_timeframes?.join(', ')}</div></div>
              <div><div className="text-slate-500 mb-1">Default Timeframe</div><div>{snapshot.settings.default_timeframe}</div></div>
              <div><div className="text-slate-500 mb-1">Default Chart</div><div>{snapshot.settings.default_chart_type}</div></div>
              <div className="md:col-span-2"><div className="text-slate-500 mb-1">Enabled Indicators</div><div>{snapshot.settings.enabled_indicators?.join(', ')}</div></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebTraderPreview;
